import { useEffect, useRef, useState } from 'react'

/**
 * A 1 Hz elapsed-seconds clock anchored to the SERVER's time (G2.3, G2.4, G2.5).
 *
 * Three properties, each of which is a defect this hook exists to prevent:
 *
 *  1. IT TICKS INDEPENDENTLY OF THE DATA POLL. The batch page polls every 5 seconds. A
 *     timer rendered from the polled payload alone would sit still and then jump five
 *     seconds, which reads as a frozen page. So the tick is its own second-aligned,
 *     self-rescheduling setTimeout — the same pattern components/UserClock.tsx uses — and
 *     the poll only ever corrects it.
 *
 *  2. IT IS ANCHORED TO THE SERVER, NOT THE BROWSER. A client clock minutes off would
 *     otherwise display a wrong figure with total confidence. `serverNow` arrives with every
 *     poll; the offset between it and the browser's clock is recomputed each time, so skew
 *     is corrected continuously rather than once at mount.
 *
 *  3. IT IS BOUNDED. Clamped into [0, maxSeconds]. It cannot go negative (a clock
 *     adjustment), it cannot roll over, and it cannot grow without bound.
 *
 * When `endIso` is supplied the measurement is CLOSED: the value is fixed and the timer
 * stops ticking, because a finished batch's elapsed time is a stored fact rather than a
 * running one.
 */
export const DAY_SECONDS = 24 * 60 * 60

export type UseElapsedArgs = {
  /** Start of the measurement, ISO-8601. `FaxBatch.t_created` for the batch timer. */
  startIso?: string | null
  /** End of the measurement once closed, ISO-8601. Null while it is still running. */
  endIso?: string | null
  /** The server's clock as of the last poll, ISO-8601. */
  serverNowIso?: string | null
  /** Upper bound, in seconds. Defaults to 24 hours. */
  maxSeconds?: number
}

export function useElapsed({
  startIso,
  endIso,
  serverNowIso,
  maxSeconds = DAY_SECONDS,
}: UseElapsedArgs): number | null {
  // Milliseconds to ADD to the browser's clock to get the server's. Recomputed on every
  // poll rather than captured at mount, so a long-lived tab does not drift.
  const skewRef = useRef(0)
  const [, setTick] = useState(0)

  useEffect(() => {
    if (!serverNowIso) return
    const server = Date.parse(serverNowIso)
    if (Number.isNaN(server)) return
    skewRef.current = server - Date.now()
    // Re-render immediately on a correction so the displayed figure does not wait up to a
    // second to reflect the server's answer.
    setTick(t => t + 1)
  }, [serverNowIso])

  const closed = Boolean(endIso)

  useEffect(() => {
    if (closed || !startIso) return
    let timer: number
    const tick = () => {
      setTick(t => t + 1)
      timer = window.setTimeout(tick, 1000 - (Date.now() % 1000))
    }
    timer = window.setTimeout(tick, 1000 - (Date.now() % 1000))
    return () => window.clearTimeout(timer)
  }, [closed, startIso])

  if (!startIso) return null
  const start = Date.parse(startIso)
  if (Number.isNaN(start)) return null

  let end: number
  if (endIso) {
    const parsed = Date.parse(endIso)
    if (Number.isNaN(parsed)) return null
    end = parsed
  } else {
    end = Date.now() + skewRef.current
  }

  return Math.max(0, Math.min((end - start) / 1000, maxSeconds))
}

/** `1:04:09` / `4:09` — a duration a human reads at a glance, zero-padded below the hour. */
export function formatElapsed(seconds: number | null): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—'
  const total = Math.floor(Math.max(0, seconds))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}
